import { Injectable, computed, inject, signal } from "@angular/core";
import {
  injectMutation,
  injectQuery,
  injectQueryClient,
  queryOptions,
} from "@ngneat/query";
import { derivedAsync } from "ngxtension/derived-async";
import {
  AvailableDatesPayload,
  OrderReschedulePayload,
  OrderRescheduleResponse,
} from "../models/order-reschedule.model";
import { OrderRescheduleService } from "./order-reschedule.service";
import { sleep } from "radash";

@Injectable({ providedIn: "root" })
export class OrderRescheduleState {
  private readonly service = inject(OrderRescheduleService);
  private readonly query = injectQuery();
  private readonly mutation = injectMutation();
  private readonly queryClient = injectQueryClient();

  readonly availableDatesPayload = signal<AvailableDatesPayload | null>(null);
  readonly maxVisibleDates = signal(7);
  private readonly DEFAULT_VISIBLE_DATES = 7;

  readonly mutationResponse = signal<OrderRescheduleResponse | null>(null);
  readonly mutationError = signal<any>(null);

  private getAvailableDatesOptions(tenantId: string, orderId: string) {
    return queryOptions({
      queryKey: [
        "order-reschedule",
        "available-dates",
        tenantId,
        orderId,
      ] as const,
      queryFn: async () => {
        return await this.service.fetchAvailableDates(tenantId, orderId);
      },
      enabled: !!tenantId && !!orderId,
    });
  }

  readonly availableDatesQuery = derivedAsync(() => {
    const payload = this.availableDatesPayload();
    const tenantId = payload?.tenantId;
    const orderId = payload?.orderId;

    return this.query({
      ...this.getAvailableDatesOptions(tenantId!, orderId!),
    }).result$;
  });

  readonly isLoading = computed(() => {
    const query = this.availableDatesQuery();
    if (!query) return false;
    return query.isLoading;
  });

  readonly availableDates = computed(() => {
    const query = this.availableDatesQuery();
    if (!query) return [];
    return query.data ?? [];
  });

  readonly isError = computed(() => {
    const query = this.availableDatesQuery();
    if (!query) return null;
    return query.error ? (query.error as Error).message : null;
  });

  readonly isAllowedToReschedule = computed(() => {
    const dates = this.availableDates();
    return dates.length > 0;
  });

  readonly canRetry = computed(() => {
    return !!this.isError();
  });

  readonly visibleDates = computed(() => {
    const dates = this.availableDates();
    const maxVisible = this.maxVisibleDates();
    return dates.slice(0, maxVisible);
  });

  readonly isShowingAllDates = computed(() => {
    const maxVisible = this.maxVisibleDates();
    const totalDates = this.availableDates().length;
    return maxVisible >= totalDates;
  });

  readonly isLoaded = computed(() => {
    return !this.isLoading() && this.availableDates().length > 0;
  });

  readonly rescheduleMutation = this.mutation({
    mutationFn: async (payload: OrderReschedulePayload) => {
      this.mutationError.set(null);
      this.mutationResponse.set(null);
      return await this.service.rescheduleOrder(payload);
    },
    onSuccess: async (response: OrderRescheduleResponse) => {
      this.mutationResponse.set(response);

      if (response.code === 1) {
        await sleep(2000);
        await this.queryClient.invalidateQueries({
          queryKey: ["orders"],
        });
        await this.queryClient.invalidateQueries({
          queryKey: ["order-detail"],
          exact: false,
        });

        await this.queryClient.invalidateQueries({
          queryKey: ["services"],
          exact: false,
        });
      }
    },
    onError: (error: any) => {
      this.mutationError.set(error);
    },
  });

  readonly canRetrySubmit = computed(() => {
    const mutation = this.rescheduleMutation.result$;
    return !!mutation;
  });

  readonly isBusinessRuleError = computed(() => {
    const response = this.mutationResponse();
    return response?.code === 2;
  });

  readonly isGeneralMutationError = computed(() => {
    const response = this.mutationResponse();
    const apiError = this.mutationError();
    return (
      (response && response.code !== 1 && response.code !== 2) || !!apiError
    );
  });

  showMoreDates() {
    this.maxVisibleDates.set(this.availableDates().length);
  }

  showFewerDates() {
    this.maxVisibleDates.set(this.DEFAULT_VISIBLE_DATES);
  }

  retry() {
    const payload = this.availableDatesPayload();
    if (payload?.tenantId && payload?.orderId) {
      this.queryClient.invalidateQueries({
        queryKey: this.getAvailableDatesOptions(
          payload.tenantId,
          payload.orderId,
        ).queryKey,
      });
    }
  }

  rescheduleOrder(payload: OrderReschedulePayload) {
    return this.rescheduleMutation.mutate(payload);
  }

  resetSubmitState() {
    this.mutationError.set(null);
    this.mutationResponse.set(null);
    this.rescheduleMutation.reset();
  }
}
